/**
 * Whether a raw Firestore clientPortals snapshot shows design fee paid for the
 * current view. Per-project `projects.{dealId}.designFeePaid` is authoritative
 * in multi-project portals; root `designFeePaid` only applies to `dealId` when
 * no other project row is marked paid (avoids cross-project bleed).
 */
export function anyOtherPortalProjectDesignFeePaid(
  data: Record<string, any> | undefined,
  projectId: string,
): boolean {
  const projects = data?.projects;
  if (!projects || typeof projects !== 'object') return false;
  return Object.entries(projects).some(
    ([id, row]) => id !== projectId && !!(row as Record<string, any>)?.designFeePaid,
  );
}

/** Root-level payment flag applies to this project (legacy dual-write / primary deal). */
export function portalRootDesignFeePaidAppliesToProject(
  data: Record<string, any> | undefined,
  projectId: string | null | undefined,
): boolean {
  if (!data?.designFeePaid) return false;
  const pid = projectId || null;
  if (!pid) return true;
  if (data.projects?.[pid]?.designFeePaid) return true;
  if (anyOtherPortalProjectDesignFeePaid(data, pid)) return false;
  return data.dealId === pid;
}

export function snapshotShowsDesignFeePaid(
  data: Record<string, any> | undefined,
  projectId: string | null | undefined,
): boolean {
  if (!data) return false;
  const pid = projectId || null;
  if (pid) {
    if (data.projects?.[pid]?.designFeePaid) return true;
    return portalRootDesignFeePaidAppliesToProject(data, pid);
  }
  return !!data.designFeePaid;
}

/** Whether staff should assign a project number (design fee paid or under contract, no number yet). */
export function projectNeedsNumberAssignment(
  deal: { projectNumber?: string | null; outcome?: string } | null | undefined,
  portalProject: { designFeePaid?: boolean; designFeeSkipped?: boolean } | null | undefined,
  opts?: { underContract?: boolean },
): boolean {
  if (!deal) return false;
  if ((deal.projectNumber || '').trim()) return false;
  if (opts?.underContract || deal.outcome === 'won') return true;
  if (!portalProject?.designFeePaid) return false;
  if (portalProject.designFeeSkipped) return false;
  return true;
}

/** Agreement fields for PDF / signed-agreement UI (per-project when selected). */
export function getPortalAgreementSnapshot(
  data: Record<string, any>,
  projectId: string | null | undefined,
): {
  agreementSigned: boolean;
  agreementSignedAt?: unknown;
  agreementSignedBy?: string;
  agreementSignature?: string;
  agreementServices?: { name: string; price: number }[];
  agreementTotal?: number;
  selectedServices?: string[];
} {
  const pid = projectId || null;
  const row =
    pid && data.projects?.[pid] ? (data.projects[pid] as Record<string, any>) : data;
  return {
    agreementSigned: !!row.agreementSigned,
    agreementSignedAt: row.agreementSignedAt,
    agreementSignedBy: row.agreementSignedBy,
    agreementSignature: row.agreementSignature,
    agreementServices: row.agreementServices,
    agreementTotal: row.agreementTotal,
    selectedServices: row.selectedServices ?? data.selectedServices,
  };
}
